<template>
  <div class="verify-license-page">
    <header class="page-header">
      <h1>Verify your veterinary license</h1>
      <p class="lede">
        Free, unlimited access to the clinical evidence agent &amp; CE library
        for verified DVMs. SCAV / association membership is a benefit layer
        on top — not the access wall.
      </p>
    </header>

    <section v-if="status?.is_verified_clinician" class="status status-verified">
      <h2>You're verified</h2>
      <p>
        License: <strong>{{ status.license_number }}</strong>
        <span v-if="status.license_state"> ({{ status.license_state }})</span>
      </p>
      <p v-if="status.verified_at">
        Verified on {{ new Date(status.verified_at).toLocaleDateString() }}
      </p>
      <router-link to="/lms-agent" class="btn btn-primary">
        Open the consult agent
      </router-link>
    </section>

    <section v-else class="verify-form-wrap">
      <div class="method-tabs">
        <button
          v-for="m in methods"
          :key="m.id"
          :class="['tab', { active: method === m.id }]"
          @click="method = m.id"
        >{{ m.label }}</button>
      </div>

      <form class="verify-form" @submit.prevent="submit">
        <label v-if="method !== 'manual_upload'">
          Name on license
          <input v-model="form.license_holder_name" type="text" autocomplete="name" />
        </label>

        <template v-if="method === 'avma'">
          <label>
            AVMA member number
            <input v-model="form.avma_member_number" type="text" required />
          </label>
        </template>

        <template v-else-if="method === 'state_board'">
          <label>
            State (2-letter)
            <input v-model="form.license_state" type="text" maxlength="2" required />
          </label>
          <label>
            License number
            <input v-model="form.license_number" type="text" required />
          </label>
        </template>

        <template v-else-if="method === 'manual_upload'">
          <label>
            DVM credential document URL
            <input v-model="form.document_url" type="url" required
                   placeholder="https://..." />
          </label>
          <p class="hint">
            Upload a PDF/JPG of your DVM diploma, license certificate, or AVMA
            membership card to your cloud drive and paste the public link.
          </p>
        </template>

        <button class="btn btn-primary" :disabled="busy" type="submit">
          {{ busy ? 'Submitting…' : 'Submit for verification' }}
        </button>
      </form>

      <div v-if="message" class="result" :class="{ pending: pending, error: errorMsg }">
        {{ message }}
      </div>

      <aside class="benefit-panel">
        <h3>Why we verify</h3>
        <ul>
          <li>Free, unlimited evidence-based consults for working clinicians</li>
          <li>CE library with state-board accepted credit</li>
          <li>Mobile app with offline saved consults</li>
        </ul>
        <p class="scav-cta">
          <strong>SCAV member?</strong> Verification is automatic via your
          SCAV login. Not a member?
          <router-link to="/register-scav">Join SCAV</router-link>
          for free CE and member discounts on top of unlimited access.
        </p>
      </aside>
    </section>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'VerifyVetLicense',
  data() {
    return {
      method: 'avma',
      methods: [
        { id: 'avma', label: 'AVMA member number' },
        { id: 'state_board', label: 'State board license' },
        { id: 'manual_upload', label: 'Upload credential' },
      ],
      form: {
        license_holder_name: '',
        avma_member_number: '',
        license_number: '',
        license_state: '',
        document_url: '',
      },
      status: null,
      busy: false,
      message: '',
      pending: false,
      errorMsg: false,
    }
  },
  async mounted() {
    try {
      const res = await api.get('/users/vet-license/status/')
      this.status = res.data
    } catch (e) {
      this.status = null
    }
  },
  methods: {
    async submit() {
      this.busy = true
      this.message = ''
      this.pending = false
      this.errorMsg = false
      try {
        const payload = { method: this.method, ...this.form }
        const res = await api.post('/users/vet-license/verify/', payload)
        this.message = res.data.message || 'Submitted.'
        this.pending = !!res.data.pending_review
        if (res.data.verified) {
          this.status = await api.get('/users/vet-license/status/').then(r => r.data)
        }
      } catch (err) {
        this.errorMsg = true
        this.message = err?.response?.data?.error || 'Submission failed.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.verify-license-page {
  max-width: 880px;
  margin: 2rem auto;
  padding: 1.5rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.page-header h1 { margin-bottom: .5rem; }
.lede { color: #475569; max-width: 56ch; }
.method-tabs { display: flex; gap: .5rem; margin: 1.5rem 0 1rem; border-bottom: 1px solid #e5e7eb; }
.tab {
  padding: .6rem 1rem;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  color: #475569;
  font-weight: 500;
}
.tab.active { border-bottom-color: #0f766e; color: #0f766e; }
.verify-form { display: grid; gap: .9rem; max-width: 480px; }
.verify-form label { display: grid; gap: .25rem; font-weight: 500; color: #1f2937; }
.verify-form input {
  padding: .55rem .7rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 1rem;
}
.btn-primary {
  background: #0f766e;
  color: white;
  border: 0;
  padding: .65rem 1.2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  width: max-content;
}
.btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.result { margin-top: 1rem; padding: .8rem 1rem; border-radius: 6px; background: #ecfdf5; color: #065f46; }
.result.pending { background: #fef3c7; color: #78350f; }
.result.error { background: #fee2e2; color: #991b1b; }
.status-verified {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 12px;
}
.benefit-panel {
  margin-top: 2rem;
  padding: 1.25rem 1.5rem;
  background: #f8fafc;
  border-left: 4px solid #0f766e;
  border-radius: 8px;
}
.benefit-panel h3 { margin-top: 0; }
.benefit-panel ul { margin: .5rem 0 1rem 1rem; padding: 0; color: #1f2937; }
.benefit-panel li { margin-bottom: .35rem; }
.scav-cta { color: #334155; font-size: .95rem; }
.hint { color: #64748b; font-size: .85rem; }
</style>
